import { useCallback, useEffect, useRef, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';

import { adsProvider } from '../../providers/adsProvider';
import type { AdModel } from '../../types/ads';

import { Loader, MyButton } from '../uiComponents';
import { PropertyAdEditor } from './PropertyAdEditor';

type EditEntryState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'editor'; propertyAd: AdModel };

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Unable to load this listing. Please try again.';

export const EditPropertyAdV2 = () => {
  const { id } = useParams<{ id: string }>();
  const [entryState, setEntryState] = useState<EditEntryState>({ status: 'loading' });
  const isMountedRef = useRef(true);
  const requestInFlightRef = useRef(false);

  const loadPropertyAd = useCallback(async () => {
    if (requestInFlightRef.current) return;

    if (!id) {
      setEntryState({ status: 'error', message: 'Listing ID is missing.' });
      return;
    }

    requestInFlightRef.current = true;
    setEntryState({ status: 'loading' });

    try {
      const response = await adsProvider.getAdById(id);
      if (!isMountedRef.current) return;

      if (response.ok && response.data) {
        setEntryState({ status: 'editor', propertyAd: response.data });
        return;
      }

      const fallbackMessage =
        response.status === 404
          ? 'This listing was not found.'
          : response.status === 403
            ? 'You do not have permission to edit this listing.'
            : 'Unable to load this listing. Please try again.';
      setEntryState({ status: 'error', message: response.message || fallbackMessage });
    } catch (error) {
      if (isMountedRef.current) {
        setEntryState({ status: 'error', message: getErrorMessage(error) });
      }
    } finally {
      requestInFlightRef.current = false;
    }
  }, [id]);

  useEffect(() => {
    isMountedRef.current = true;
    void loadPropertyAd();

    return () => {
      isMountedRef.current = false;
    };
  }, [loadPropertyAd]);

  if (entryState.status === 'loading') {
    return (
      <Stack
        alignItems='center'
        aria-label='Loading listing'
        justifyContent='center'
        minHeight={280}
        padding='32px 16px'
        role='status'
        width='100%'
      >
        <Loader />
      </Stack>
    );
  }

  if (entryState.status === 'error') {
    return (
      <Stack
        alignItems='center'
        component='section'
        justifyContent='center'
        minHeight={280}
        padding='32px 16px'
        role='alert'
        textAlign='center'
        width='100%'
      >
        <Typography color='#2a3842' component='h1' fontSize={22} variant='h5'>
          Unable to open PropertyAd Editor
        </Typography>
        <Typography
          color='#6f7d87'
          component='p'
          fontWeight={400}
          margin='10px 0 20px'
          maxWidth={520}
        >
          {entryState.message}
        </Typography>
        <MyButton
          data={{
            buttonName: 'Retry',
            buttonType: 'button',
            customWidth: '120px',
            variant: 'contained',
          }}
          onClick={() => void loadPropertyAd()}
        />
      </Stack>
    );
  }

  return <PropertyAdEditor mode='edit' propertyAd={entryState.propertyAd} />;
};
